import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  state as reactiveState,
  useReactiveState,
} from '@frameio/graphql-client';
import type {
  PaymentCard,
  PaymentMethod,
  PlanFeatureInput,
  PlanPeriod,
  PlanTier,
} from '@frameio/graphql-client/generated';
import { useGetPaymentMethodQuery } from '@frameio/graphql-client/generated';
import type { StripeData } from '@frameio/graphql-client/graphql/features/billing/state/state';
import { useActiveAccountId } from '@frameio/shared/contexts';
import {
  useAccountRoleTracking,
  useCurrentPlanTracking,
} from '@frameio/shared/plans/hooks';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import {
  Dialog,
  DialogHeader,
  LoadingContainer,
  theme,
  ThemeProvider,
  usePrevious,
} from '@frameio/vapor';
import type {
  SubscriptionUpdateSource,
  UsageLimitExceeded,
} from '../../../feature-gate/config';
import { segmentTrackingValues } from '../../../feature-gate/config';
import { STORAGE_VARIANT_CONFIG } from '../../../plans/components/storageVariantConfig';
import { UPGRADE_DIALOG_STYLE } from '../../../shared/components/UpgradeDialogLayout';
import { ACCOUNT_CONVERSION_STEPS } from './constants';
import TEXT from './generated/TEXT/ConversionDialog';
import { useTrackModalOpened } from './hooks/useTrackModalOpened';
import { ManageStorage } from './ManageStorage';
import { PlanDetails } from './PlanDetails';
import { PlanPayment } from './PlanPayment';
import type { PlanQuoteAddOns } from './PlanSummary';
import { PlanSummary } from './PlanSummary';
import type { ConversionDialogProps, StorageVariant } from './types';

TEXT.DEFINE({
  /** Header title for payment details step */
  paymentDetails: 'Payment Details',
  /** Subtitle text for payment step indicating required fields */
  requiredFields: 'All fields are required unless marked optional.',
  /** Header title for summary step */
  summary: 'Summary',
  /** Header title for upgrade plan step */
  upgradePlan: 'Upgrade Plan',
  /** Loading label for content */
  loadingLabel: 'Loading content...',
  /** Accessibility label for dialog close button */
  dialogCloseLabel: 'Close Dialog',
});

const getHeaderTitle = (
  step: ACCOUNT_CONVERSION_STEPS,
  storageVariant: StorageVariant
) => {
  switch (step) {
    case ACCOUNT_CONVERSION_STEPS.MANAGE_STORAGE:
      return STORAGE_VARIANT_CONFIG[storageVariant].manageStorageLabel;
    case ACCOUNT_CONVERSION_STEPS.PLAN_DETAILS:
      return TEXT.upgradePlan;
    case ACCOUNT_CONVERSION_STEPS.SUMMARY:
      return TEXT.summary;
    case ACCOUNT_CONVERSION_STEPS.PAYMENT:
      return TEXT.paymentDetails;
    default:
      return '';
  }
};

type RenderStepProps = {
  accountId: string;
  cost: number;
  feature?: PlanFeatureInput;
  featureGateType?: string;
  handleClose: () => void;
  id: string;
  isLoading: boolean;
  step: ACCOUNT_CONVERSION_STEPS;
  paymentMethod?: PaymentCard;
  period: PlanPeriod;
  planQuoteAddOns: PlanQuoteAddOns | null;
  previousStep?: ACCOUNT_CONVERSION_STEPS;
  setStep: Dispatch<SetStateAction<ACCOUNT_CONVERSION_STEPS>>;
  setPlanQuoteAddOns: (addOns: PlanQuoteAddOns | null) => void;
  shouldUpgradeSubscriptionImmediately: boolean;
  storageVariant: StorageVariant;
  subscriptionUpdateSource?: SubscriptionUpdateSource;
  tier?: PlanTier;
  title: string;
  usageLimitExceeded?: UsageLimitExceeded;
  userLimit?: number | null;
  isDiscountPlan?: boolean;
  isPausedPlan?: boolean;
  pauseOfferDurationMonths?: number;
};

const renderAccountConversionStep = ({
  accountId,
  cost,
  feature,
  featureGateType,
  handleClose,
  id,
  isLoading,
  step,
  paymentMethod,
  period,
  planQuoteAddOns,
  previousStep,
  setStep,
  setPlanQuoteAddOns,
  shouldUpgradeSubscriptionImmediately,
  storageVariant,
  subscriptionUpdateSource,
  tier,
  title,
  usageLimitExceeded,
  userLimit,
  isDiscountPlan,
  isPausedPlan,
  pauseOfferDurationMonths,
}: RenderStepProps) => {
  switch (step) {
    case ACCOUNT_CONVERSION_STEPS.MANAGE_STORAGE:
      return (
        <ManageStorage
          accountId={accountId}
          onCancel={handleClose}
          period={period}
          planQuoteAddOns={planQuoteAddOns ?? undefined}
          setParentStep={setStep}
          setPlanQuoteAddOns={setPlanQuoteAddOns}
          variant={storageVariant}
        />
      );

    case ACCOUNT_CONVERSION_STEPS.PLAN_DETAILS:
      return (
        <PlanDetails
          cost={cost}
          feature={feature}
          featureGateType={featureGateType}
          isLoading={isLoading}
          onClose={handleClose}
          paymentMethod={paymentMethod}
          period={period}
          setParentStep={setStep}
          subscriptionUpdateSource={subscriptionUpdateSource}
          tier={tier}
          title={title}
        />
      );
    case ACCOUNT_CONVERSION_STEPS.SUMMARY:
      return (
        <PlanSummary
          feature={feature}
          featureGateType={featureGateType}
          isDiscountPlan={isDiscountPlan}
          isPausedPlan={isPausedPlan}
          onCancel={handleClose}
          pauseOfferDurationMonths={pauseOfferDurationMonths}
          paymentMethod={paymentMethod}
          period={period}
          planId={id}
          planQuoteAddOns={planQuoteAddOns}
          setParentStep={setStep}
          shouldUpgradeSubscriptionImmediately={
            shouldUpgradeSubscriptionImmediately
          }
          subscriptionUpdateSource={subscriptionUpdateSource}
          title={title}
          usageLimitExceeded={usageLimitExceeded}
          userLimit={userLimit}
        />
      );
    case ACCOUNT_CONVERSION_STEPS.PAYMENT:
      return (
        <PlanPayment
          onCancel={handleClose}
          paymentMethod={paymentMethod}
          period={period}
          previousStep={previousStep}
          setParentStep={setStep}
          subscriptionUpdateSource={subscriptionUpdateSource}
        />
      );

    default:
      return null;
  }
};

function getFirstStep(
  paymentMethod?: PaymentMethod | null,
  shouldShowSelectedPlan = false,
  shouldShowManageStorage = false,
  stripeData?: StripeData
) {
  if (shouldShowManageStorage) {
    return ACCOUNT_CONVERSION_STEPS.MANAGE_STORAGE;
  }

  if (shouldShowSelectedPlan) {
    return ACCOUNT_CONVERSION_STEPS.PLAN_DETAILS;
  }
  if (
    (stripeData?.addressElementValue && stripeData?.stripeTokenId) ||
    (paymentMethod as PaymentCard)?.card
  ) {
    return ACCOUNT_CONVERSION_STEPS.SUMMARY;
  }

  return ACCOUNT_CONVERSION_STEPS.PAYMENT;
}

const upgradeModalStylesEnterprise = {
  ...UPGRADE_DIALOG_STYLE,
  maxWidth: '925px',
};

function getUpgradeModalStyles(tier = 'PRO') {
  if (tier === 'ENTERPRISE') {
    return upgradeModalStylesEnterprise;
  }

  return UPGRADE_DIALOG_STYLE;
}

const stepTracking = {
  [ACCOUNT_CONVERSION_STEPS.MANAGE_STORAGE]: 'ManageStorage',
  [ACCOUNT_CONVERSION_STEPS.PAYMENT]: 'BillingInfo',
  [ACCOUNT_CONVERSION_STEPS.PLAN_DETAILS]: 'UpgradeModal',
  [ACCOUNT_CONVERSION_STEPS.SUMMARY]: 'Summary',
};

export const ConversionDialog = ({
  cost,
  feature,
  featureGateType,
  id,
  isShowing,
  onExited,
  period,
  setIsShowing,
  shouldShowSelectedPlan = false,
  shouldShowManageStorage = false,
  shouldUpgradeSubscriptionImmediately = true,
  storageVariant = 'active',
  subscriptionUpdateSource,
  tier,
  title,
  userLimit,
  usageLimitExceeded,
  isDiscountPlan = false,
  isPausedPlan = false,
  pauseOfferDurationMonths,
  zIndex = theme.zIndices.modal + 1,
}: ConversionDialogProps) => {
  const cachedStripeData = useReactiveState(reactiveState.billing.stripe);

  const accountId = useActiveAccountId() ?? '';

  const hasCalledOpenTracking = useRef(false);

  const { data: paymentMethodData, loading } = useGetPaymentMethodQuery({
    variables: { accountId },
    skip: !accountId || !isShowing,
  });

  const paymentMethod = paymentMethodData?.account?.subscription?.paymentMethod;

  const paymentMethodCard =
    paymentMethod?.__typename === 'PaymentCard' ? paymentMethod : undefined;

  const firstStep = getFirstStep(
    paymentMethod,
    shouldShowSelectedPlan,
    shouldShowManageStorage,
    cachedStripeData
  );

  const [step, setStep] = useState(firstStep);
  const previousStep = usePrevious(firstStep);

  const [planQuoteAddOns, setPlanQuoteAddOns] =
    useState<PlanQuoteAddOns | null>(null);

  const currentPlanTracking = useCurrentPlanTracking();
  const accountRoleTracking = useAccountRoleTracking();

  const track = useTrackUnsafe();

  const handleClose = useCallback(
    (shouldTrackExit = true) => {
      if (shouldTrackExit) {
        if (feature) {
          track('upgrade-flow-exited', {
            ...currentPlanTracking,
            ...accountRoleTracking,
            feature: segmentTrackingValues.features[feature],
            feature_gate_type: featureGateType,
            step: stepTracking[step],
            subscription_update_source: subscriptionUpdateSource,
          });
        } else {
          track('checkout-exited', {
            ...currentPlanTracking,
            ...accountRoleTracking,
            step: stepTracking[step],
            subscription_update_source: subscriptionUpdateSource,
          });
        }
      }
      reactiveState.billing.stripe.set({});
      setIsShowing(false);
      hasCalledOpenTracking.current = false;
    },
    [
      currentPlanTracking,
      accountRoleTracking,
      feature,
      featureGateType,
      setIsShowing,
      step,
      subscriptionUpdateSource,
      track,
    ]
  );

  const isUpgradeModalPage = step === ACCOUNT_CONVERSION_STEPS.PLAN_DETAILS;

  const headerTitle = getHeaderTitle(step, storageVariant);
  const subtitle =
    step === ACCOUNT_CONVERSION_STEPS.PAYMENT ? TEXT.requiredFields : '';

  const shouldRenderDialogHeader =
    !!headerTitle && headerTitle !== '' && !isUpgradeModalPage;

  const size = isUpgradeModalPage ? 'large' : 'medium';

  const handleOnExited = useCallback(() => {
    if (onExited) {
      onExited();
    }

    setPlanQuoteAddOns(null);
  }, [onExited]);

  const trackModalOpened = useTrackModalOpened();

  useEffect(() => {
    // this prevents a jump in modal size when loading the upgrade modal
    if (isShowing && shouldShowSelectedPlan) {
      setStep(firstStep);
    }

    if (isShowing && !loading && !hasCalledOpenTracking.current) {
      setStep(firstStep);
      trackModalOpened({
        feature,
        featureGateType,
        firstStep,
        subscriptionUpdateSource,
        usageLimitExceeded,
      });
      hasCalledOpenTracking.current = true;
    }
  }, [
    feature,
    featureGateType,
    firstStep,
    isShowing,
    loading,
    shouldShowSelectedPlan,
    subscriptionUpdateSource,
    trackModalOpened,
    usageLimitExceeded,
  ]);

  return (
    <ThemeProvider brand="fio">
      <Dialog
        aria-labelledby={headerTitle}
        isShowing={isShowing}
        onExited={handleOnExited}
        onRequestClose={() => handleClose()}
        size={size}
        style={isUpgradeModalPage ? getUpgradeModalStyles(tier) : undefined}
        zIndex={zIndex}
      >
        <LoadingContainer
          isLoading={loading && !isUpgradeModalPage}
          loadingLabel={TEXT.loadingLabel}
        >
          {shouldRenderDialogHeader && (
            <DialogHeader
              closeLabel={TEXT.dialogCloseLabel}
              subtitle={subtitle}
              title={headerTitle}
            />
          )}
          {renderAccountConversionStep({
            accountId,
            planQuoteAddOns,
            setPlanQuoteAddOns,
            cost,
            feature,
            featureGateType,
            handleClose,
            id,
            isLoading: loading,
            step,
            paymentMethod: paymentMethodCard,
            period,
            previousStep,
            setStep,
            shouldUpgradeSubscriptionImmediately,
            storageVariant,
            subscriptionUpdateSource,
            tier,
            title,
            usageLimitExceeded,
            userLimit,
            isDiscountPlan,
            isPausedPlan,
            pauseOfferDurationMonths,
          })}
        </LoadingContainer>
      </Dialog>
    </ThemeProvider>
  );
};
